import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import apiService from "../../services/apiService";
import "./DRDocsTab.css";
import { API_CONFIG } from "../../config/api";

const DRDocsTab = () => {
  const { id } = useParams();
  const [round2Items, setRound2Items] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openMain, setOpenMain] = useState(true);
  const [openSub, setOpenSub] = useState({});
  const [latestZip, setLatestZip] = useState(null);
  const [loadingZip, setLoadingZip] = useState(false);

  useEffect(() => {
    if (id) fetchDataRoomItems();
    fetchLatestZip();
  }, [id]);

  const fetchDataRoomItems = async () => {
    try {
      setLoading(true);
      const data = await apiService.get(
        `/property-data-room/admin?propertyId=${id}&round=2`
      );
      setRound2Items(data || []);
      setError(null);
    } catch (err) {
      setError("Failed to load property data room items");
    } finally {
      setLoading(false);
    }
  };

  const fetchLatestZip = async () => {
    try {
      setLoadingZip(true);
      const data = await apiService.get(
        `/property-data-room/latest-zip/${id}/2`
      );
      setLatestZip(data);
    } catch (err) {
      console.error("Error fetching latest zip:", err);
      setLatestZip(null);
    } finally {
      setLoadingZip(false);
    }
  };

  const handleZipDownload = async () => {
    if (!latestZip || !latestZip.id) return;

    try {
      const response = await fetch(
        `${API_CONFIG.BASE_URL}/property-data-room/download-zip/${latestZip.id}`,
        {
          method: "GET",
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const contentDisposition = response.headers.get("Content-Disposition");
      let filename = latestZip.fileDescription || "round2.zip";

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) filename = filenameMatch[1];
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log(`Zip file "${filename}" downloaded successfully`);
    } catch (error) {
      console.error("Download error:", error);
      alert(`Failed to download zip file. Please try again.`);
    }
  };

  const handleFileUpload = async (event, item) => {
    const files = Array.from(event.target.files);
    if (!files || files.length === 0) {
      alert("Please select at least one valid file.");
      event.target.value = "";
      return;
    }

    // Filter out empty files
    const validFiles = files.filter(file => file && file.size > 0);
    if (validFiles.length === 0) {
      alert("Please select at least one valid file.");
      event.target.value = "";
      return;
    }

    const totalFiles = validFiles.length;
    let successCount = 0;
    let failCount = 0;

    // Upload files one at a time
    for (let i = 0; i < validFiles.length; i++) {
      const file = validFiles[i];
      try {
        const formData = new FormData();
        formData.append("UserId", "4");
        formData.append("PropertyDataRoomId", item.id.toString());
        formData.append("PropertyId", id.toString());
        formData.append("File", file, file.name);

        await apiService.uploadFile("/property-data-room", formData);
        successCount++;
        
        // Show progress for multiple files
        if (totalFiles > 1) {
          console.log(`File ${i + 1} of ${totalFiles} ("${file.name}") uploaded successfully`);
        }
      } catch (err) {
        failCount++;
        console.error(`Error uploading file "${file.name}":`, err);
        // Continue with next file instead of stopping
      }
    }

    // Show final status
    event.target.value = "";
    
    if (successCount === totalFiles) {
      alert(`All ${totalFiles} file${totalFiles > 1 ? 's' : ''} uploaded successfully!`);
      fetchDataRoomItems();
    } else if (successCount > 0) {
      alert(`${successCount} file${successCount > 1 ? 's' : ''} uploaded successfully. ${failCount} file${failCount > 1 ? 's' : ''} failed.`);
      fetchDataRoomItems();
    } else {
      alert(`Failed to upload all files. Please try again.`);
    }
  };

  const handleFileDownload = async (item) => {
    try {
      const response = await fetch(
        `${API_CONFIG.BASE_URL}/property-data-room/download/${item.fileId}`,
        {
          method: "GET",
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const contentDisposition = response.headers.get("Content-Disposition");
      let filename = item.displayName;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) filename = filenameMatch[1];
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download error:", error);
      alert(`Failed to download file "${item.displayName}". Please try again.`);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const toggleMain = () => setOpenMain((prev) => !prev);
  const toggleSub = (index) =>
    setOpenSub((prev) => ({ ...prev, [index]: !prev[index] }));

  if (loading) {
    return (
      <div className="tab-content">
        <div className="loading-state">
          <p>Loading property data room items...</p>
        </div>
      </div>
    );
  }

  if (error)
    return (
      <div>
        {error} <button onClick={fetchDataRoomItems}>Retry</button>
      </div>
    );

  if (!round2Items.length) return <div>No data room items found.</div>;

  return (
    <div className="accordion-container">
      {/* ZIP DOWNLOAD SECTION */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
        }}
      >
        {latestZip && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <button
              onClick={handleZipDownload}
              title={latestZip.fileDescription}
              style={{
                backgroundColor: "transparent",
                border: "none",
                cursor: "pointer",
                fontSize: "20px",
                padding: "4px 8px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              📦
            </button>
            {latestZip.fileDescription && (
              <span style={{ color: "var(--cbre-text)", fontSize: "14px" }}>
                {latestZip.fileDescription}
              </span>
            )}
            <span style={{ color: "var(--cbre-text)", fontSize: "14px" }}>
              ({formatFileSize(latestZip.fileSize)})
            </span>
          </div>
        )}
      </div>

      {/* MAIN HEADER */}
      <button onClick={toggleMain} className="accordion-main-header">
        <span>DUE DILIGENCE DATA ROOM</span>
        <span className="arrow">
          <svg
            className={`arrow-svg ${openMain ? "open" : ""}`}
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M8 9l4 4 4-4"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>

      <div className={`accordion-main-content ${openMain ? "open" : ""}`}>
        <div className="sub-accordion-wrapper">
          {round2Items.map((section, idx) => {
            const title = section[0];
            const items = section.slice(1);

            return (
              <div key={idx} className="sub-accordion">
                <button
                  className="sub-accordion-header"
                  onClick={() => toggleSub(idx)}
                >
                  <span>{title}</span>
                  <svg
                    className={`arrow-svg ${openSub[idx] ? "open" : ""}`}
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8 9l4 4 4-4"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>

                <div
                  className={`sub-accordion-content ${
                    openSub[idx] ? "open" : ""
                  }`}
                >
                  <table className="data-room-table">
                    <tbody>
                      {items.map((item) => (
                        <tr key={item.id}>
                          <td>
                            {item.itemType === "FILE" ? (
                              <span>{item.displayName}</span>
                            ) : item.level === 3 || item.level === 4 ? (
                              <strong>{item.displayName}</strong>
                            ) : (
                              <strong style={{ color: "#538184" }}>
                                {item.displayName}
                              </strong>
                            )}
                          </td>
                          <td>
                            {item.itemType === "FILE" && (
                              <button
                                type="button"
                                className="download-btn"
                                onClick={() => handleFileDownload(item)}
                              >
                                Download
                              </button>
                            )}
                            {item.requiresDocument && item.itemType !== "FILE" && (
                              <label>
                                <input
                                  type="file"
                                  multiple
                                  style={{ display: "none" }}
                                  onChange={(e) => handleFileUpload(e, item)}
                                />
                                <span className="upload-btn">Upload</span>
                              </label>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default DRDocsTab;
